import {
  Components,
  Theme,
  ThemeOptions,
  CSSObject,
  alpha,
} from '@mui/material/styles';
import { SizeMap } from '@/themes';

// ─────────────────────────────────────────────────────────────
// TYPE DEFINITIONS
// ─────────────────────────────────────────────────────────────

type IconButtonVariantStyle = CSSObject;

const getIconButtonTheme = (theme: Theme): ThemeOptions => {
  // ─────────────────────────────────────────────────────────────
  // STYLE CONSTANTS
  // ─────────────────────────────────────────────────────────────

  // IconButton Size Map
  // Can be overridden by defining theme.size.iconButton in muiTheme.ts
  // Example: size: { iconButton: { s: { padding: '10px', fontSize: '18px', ... }, ... } }
  const iconButtonSize: SizeMap = {
    s: { padding: theme.unit(1.25) },
    m: { padding: theme.unit(2) },
    l: { padding: theme.unit(2) },
    xl: { padding: theme.unit(2.25) },
  };

  const iconButtonIconSize: SizeMap = theme.size?.iconButtonIcon || {
    s: { fontSize: '18px', width: '18px', height: '18px' },
    m: { fontSize: '20px', width: '20px', height: '20px' },
    l: { fontSize: '24px', width: '24px', height: '24px' },
    xl: { fontSize: '26px', width: '26px', height: '26px' },
  };

  // Primary Filled IconButton Styles
  const filledPrimaryStyles: IconButtonVariantStyle = {
    backgroundColor: theme.palette.M3.sys.light.primary,
    color: theme.palette.M3.sys.light['on-primary'],
    '&:hover': {
      backgroundColor: theme.palette.M3.sys.light['on-primary-container'],
    },
    '&:focus::after, &:active::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      borderRadius: 'inherit',
    },
    '&:focus::after': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-primary']['opacity-0.16'],
    },
    '&:active::after': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-primary']['opacity-0.12'],
    },
    '&.Mui-disabled': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-surface']['opacity-0.12'],
      color: alpha(theme.palette.M3.sys.light['on-surface'], 0.38),
    },
  };

  // Destructive Filled IconButton Styles
  const filledDestructiveStyles: IconButtonVariantStyle = {
    backgroundColor: theme.palette.State.error,
    color: theme.palette.State['on-error'],
    '&:hover': {
      backgroundColor: theme.palette.M3.sys.light['on-error-container'],
    },
    '&:focus::after, &:active::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      borderRadius: 'inherit',
    },
    '&:focus::after': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-primary']['opacity-0.16'],
    },
    '&:active::after': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-primary']['opacity-0.12'],
    },
    '&.Mui-disabled': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-surface']['opacity-0.12'],
      color: alpha(theme.palette.M3.sys.light['on-surface'], 0.38),
    },
  };

  // Primary Outlined IconButton Styles
  const outlinedPrimaryStyles: IconButtonVariantStyle = {
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette.M3.sys.light.outline}`,
    color: theme.palette.M3.sys.light.primary,
    '&:hover': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['primary']['opacity-0.08'],
    },
    '&:focus': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['primary']['opacity-0.12'],
      borderColor: theme.palette.M3.sys.light.primary,
    },
    '&:active': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['primary']['opacity-0.12'],
    },
    '&.Mui-disabled': {
      borderColor:
        theme.palette.M3['state-layers'].light['on-surface']['opacity-0.12'],
      color: alpha(theme.palette.M3.sys.light['on-surface'], 0.38),
    },
  };

  // Destructive Outlined IconButton Styles
  const outlinedDestructiveStyles: IconButtonVariantStyle = {
    borderColor: theme.palette.M3.sys.light.outline,
    color: theme.palette.State.error,
    '&:hover': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-primary-fixed'][
          'opacity-0.08'
        ],
    },
    '&:focus': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-primary-container'][
          'opacity-0.12'
        ],
      borderColor: theme.palette.State.error,
    },
    '&:active': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-primary-container'][
          'opacity-0.12'
        ],
      borderColor: theme.palette.State.error,
    },
    '&.Mui-disabled': {
      borderColor:
        theme.palette.M3['state-layers'].light['on-surface']['opacity-0.12'],
      color: alpha(theme.palette.M3.sys.light['on-surface'], 0.38),
    },
  };

  // Primary Standard IconButton Styles
  const standardPrimaryStyles: IconButtonVariantStyle = {
    backgroundColor: 'transparent',
    color: theme.palette.M3.sys.light.primary,
    '&:hover': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['primary']['opacity-0.08'],
    },
    '&:focus': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['primary']['opacity-0.12'],
    },
    '&:active': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['primary']['opacity-0.12'],
    },
    '&.Mui-disabled': {
      color: alpha(theme.palette.M3.sys.light['on-surface'], 0.38),
    },
  };

  // Destructive Standard IconButton Styles
  const standardDestructiveStyles: IconButtonVariantStyle = {
    color: theme.palette.State.error,
    '&:hover': { backgroundColor: theme.palette.State['error-background'] },
    '&:focus': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-primary-container'][
          'opacity-0.12'
        ],
    },
    '&:active': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-primary-container'][
          'opacity-0.12'
        ],
    },
    '&.Mui-disabled': {
      color: alpha(theme.palette.M3.sys.light['on-surface'], 0.38),
    },
  };

  // Primary Tonal IconButton Styles
  const tonalPrimaryStyles: IconButtonVariantStyle = {
    backgroundColor: theme.palette.M3.sys.light['secondary-container'],
    color: theme.palette.M3.sys.light['on-secondary-container'],
    '&:hover::after, &:focus::after, &:active::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      borderRadius: 'inherit',
    },
    '&:hover::after': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-secondary-container'][
          'opacity-0.08'
        ],
    },
    '&:focus::after': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-secondary-container'][
          'opacity-0.12'
        ],
    },
    '&:active::after': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-secondary-container'][
          'opacity-0.12'
        ],
    },
    '&.Mui-disabled': {
      backgroundColor:
        theme.palette.M3['state-layers'].light['on-surface']['opacity-0.12'],
      color: alpha(theme.palette.M3.sys.light['on-surface'], 0.38),
    },
  };

  // ─────────────────────────────────────────────────────────────
  // THEME EXPORT
  // ─────────────────────────────────────────────────────────────

  const iconButtonTheme: Components<Theme>['MuiIconButton'] = {
    defaultProps: {
      size: 's',
      disableRipple: true,
    },

    styleOverrides: {
      root: {
        borderRadius: '50%',
        boxSizing: 'border-box',
      },
    },

    variants: [
      // ─────────────────────────────
      // SIZE VARIANTS (padding + icon size)
      // ─────────────────────────────
      {
        props: { size: 's' },
        style: {
          ...iconButtonSize.s, // Spread the size config
          '& > svg': {
            ...iconButtonIconSize.s, // Spread the icon size config
          },
        },
      },
      {
        props: { size: 'm' },
        style: {
          ...iconButtonSize.m, // Spread the size config
          '& > svg': {
            ...iconButtonIconSize.m, // Spread the icon size config
          },
        },
      },
      {
        props: { size: 'l' },
        style: {
          ...iconButtonSize.l, // Spread the size config
          '& > svg': {
            ...iconButtonIconSize.l, // Spread the icon size config
          },
        },
      },
      {
        props: { size: 'xl' },
        style: {
          ...iconButtonSize.xl, // Spread the size config
          '& > svg': {
            ...iconButtonIconSize.xl, // Spread the icon size config
          },
        },
      },

      // ─────────────────────────────
      // FILLED ICON BUTTONS
      // ─────────────────────────────
      {
        props: { variant: 'filled' },
        style: filledPrimaryStyles,
      },
      {
        props: { variant: 'filled', color: 'primary' },
        style: filledPrimaryStyles,
      },

      // ─────────────────────────────
      // OUTLINED ICON BUTTONS
      // ─────────────────────────────
      {
        props: { variant: 'outlined' },
        style: outlinedPrimaryStyles,
      },
      {
        props: { variant: 'outlined', color: 'primary' },
        style: outlinedPrimaryStyles,
      },

      // ─────────────────────────────
      // STANDARD ICON BUTTONS
      // ─────────────────────────────
      {
        props: { variant: 'standard' },
        style: standardPrimaryStyles,
      },
      {
        props: { variant: 'standard', color: 'primary' },
        style: standardPrimaryStyles,
      },

      // ─────────────────────────────
      // TONAL ICON BUTTONS
      // ─────────────────────────────
      {
        props: { variant: 'tonal' },
        style: tonalPrimaryStyles,
      },
      {
        props: { variant: 'tonal', color: 'primary' },
        style: tonalPrimaryStyles,
      },

      // ─────────────────────────────
      // DESTRUCTIVE COLOR - FILLED
      // ─────────────────────────────
      {
        props: { variant: 'filled', color: 'destructive' },
        style: filledDestructiveStyles,
      },

      // ─────────────────────────────
      // DESTRUCTIVE COLOR - OUTLINED
      // ─────────────────────────────
      {
        props: { variant: 'outlined', color: 'destructive' },
        style: outlinedDestructiveStyles,
      },

      // ─────────────────────────────
      // DESTRUCTIVE COLOR - STANDARD
      // ─────────────────────────────
      {
        props: { variant: 'standard', color: 'destructive' },
        style: standardDestructiveStyles,
      },
    ],
  };

  return {
    components: { MuiIconButton: iconButtonTheme },
    size: { iconButton: iconButtonSize, iconButtonIcon: iconButtonIconSize },
  };
};

export default getIconButtonTheme;
